import { FC, useContext, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MobXProviderContext } from "mobx-react";
import { Toast } from "primereact/toast";
import { WebsiteInquiry } from "../../../models/businessHomeProfile";
import { confirmDialog } from "primereact/confirmdialog";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appInnerCardStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appWideDialogStyle,
  appConfirmDialogClasses,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

interface WebsiteInquiriesModalProps {
  inquiries: WebsiteInquiry[];
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
}

const WebsiteInquiriesModal: FC<WebsiteInquiriesModalProps> = ({
  inquiries,
  isOpen,
  toggleModal,
  flag,
  setFlag,
}) => {
  const toast = useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const onDismissSubmit = async (data: any) => {
    confirmDialog({
      message: "Are you sure you want to dismiss this inquiry? You will not be able to recover it later.",
      header: "Dismiss Confirmation",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        setErrorMessage(null);
        setLoading(true);
        try {
          await backendAPIStore.init();
          await backendAPIStore.dismissWebsiteInquiry(data.inquiryId!, data.homeId!);
        } catch (error: any) {
          setErrorMessage(error.message);
        } finally {
          setLoading(false);
          setFlag(!flag);
        }
      }
    });
  };

  const renderInquiryTypeLabel = (value: string | undefined) =>
    (value || "General")
      .toString()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const headerElement = renderSurfaceHeader(
    "Website Inquiries",
    "Review contact requests submitted through the public website and dismiss handled items.",
    "pi pi-comments",
  );

  return (
    <Dialog visible={isOpen} modal header={headerElement} style={appWideDialogStyle}
      headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
      onHide={() => { if (!isOpen) return; toggleModal(false); }}>
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#9a3412" }}>Inbox overview</div>
        <div className="mt-2" style={appMutedTextStyle}>
          Review new website inquiries in a compact inbox view with quick contact and dismiss actions.
        </div>
      </div>
      <div className="grid mb-3">
        <div className="col-12 md:col-4">
          <div style={appInnerCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Open Inquiries</div>
            <div className="mt-2" style={{ fontSize: "1.6rem", fontWeight: 700, color: "#0f172a" }}>{inquiries.length}</div>
          </div>
        </div>
        <div className="col-12 md:col-4">
          <div style={appInnerCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Latest Inquiry</div>
            <div className="mt-2" style={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>
              {inquiries[0]?.createdAt ? new Date(inquiries[0].createdAt).toLocaleString() : "No inquiries yet"}
            </div>
          </div>
        </div>
        <div className="col-12 md:col-4">
          <div style={appInnerCardStyle}>
            <div style={{ fontSize: "0.8rem", letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" }}>Quick Actions</div>
            <div className="mt-2" style={{ color: "#64748b", lineHeight: 1.7 }}>
              Contact the sender directly, then dismiss handled inquiries to keep the inbox clean.
            </div>
          </div>
        </div>
      </div>
      <div style={{ ...appFormSectionStyle, padding: "0.75rem" }}>
        {inquiries.length === 0 ? (
          <div style={{ padding: "1rem", color: "#64748b" }}>No inquiries found.</div>
        ) : (
          <div className="flex flex-column gap-3">
            {inquiries.map((inquiry, index) => (
              <div
                key={`${inquiry.inquiryId || index}-${inquiry.createdAt}`}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "20px",
                  background: "#ffffff",
                  padding: "1rem 1.1rem",
                  boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
                }}
              >
                <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                  <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                    <div className="flex flex-wrap align-items-center gap-2">
                      <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a" }}>{inquiry.fullName || "Website visitor"}</div>
                      <span
                        style={{
                          background: "#fff7ed",
                          border: "1px solid #fed7aa",
                          color: "#b45309",
                          borderRadius: "999px",
                          padding: "0.22rem 0.55rem",
                          fontSize: "0.76rem",
                          fontWeight: 700,
                        }}
                      >
                        {renderInquiryTypeLabel(inquiry.inquiryType)}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3" style={{ color: "#475569", fontSize: "0.92rem" }}>
                      {inquiry.email ? (
                        <button
                          type="button"
                          onClick={() => { window.location.href = `mailto:${inquiry.email}`; }}
                          style={{ border: "none", background: "transparent", color: "#185DA2", fontWeight: 600, padding: 0, cursor: "pointer" }}
                        >
                          <i className="pi pi-envelope mr-2" />
                          {inquiry.email}
                        </button>
                      ) : null}
                      {inquiry.phoneNumber ? (
                        <button
                          type="button"
                          onClick={() => { window.location.href = `tel:${inquiry.phoneNumber}`; }}
                          style={{ border: "none", background: "transparent", color: "#185DA2", fontWeight: 600, padding: 0, cursor: "pointer" }}
                        >
                          <i className="pi pi-phone mr-2" />
                          {inquiry.phoneNumber}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex align-items-center gap-2 ml-auto">
                    <div style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {inquiry.createdAt ? new Date(inquiry.createdAt).toLocaleString() : ""}
                    </div>
                    <Button
                      icon="pi pi-times"
                      severity="warning"
                      outlined
                      rounded
                      tooltip="Dismiss inquiry"
                      onClick={(e) => {
                        e.preventDefault();
                        onDismissSubmit(inquiry);
                      }}
                      loading={loading}
                    />
                  </div>
                </div>
                <div
                  className="mt-3"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    padding: "0.9rem 1rem",
                    color: "#334155",
                    lineHeight: 1.75,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {inquiry.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default WebsiteInquiriesModal;
